/**
 * The assets manifest (`assets-manifest.json`, scripts/lib/assets-manifest.mjs): the large files the
 * game loads (bake atlases, models, textures), each with its content hash and size. Fetched once a
 * page, revalidated with the server (it changes whenever a file does). Without it (an old build, a
 * failed request) every file is simply fetched as before.
 *
 * Safe to import from workers: it touches nothing until called.
 */

export interface AssetEntry {
  hash: string;
  size: number;
}

export interface AssetsManifest {
  version: number;
  /** By path under the game's base URL, e.g. `bake/2x/manifest.json`, `models/hero.glb`. */
  files: Record<string, AssetEntry>;
}

export const MANIFEST_FILE = 'assets-manifest.json';

/** The game's base URL, absolute (`import.meta.env.BASE_URL` is relative in this build). */
export function baseHref(): string {
  const here = globalThis.document?.baseURI ?? globalThis.location?.href ?? 'http://localhost/';
  return new URL(import.meta.env.BASE_URL, here).href;
}

/** A URL's path under the base URL (no query), or null for one outside it. */
export function assetPath(url: string): string | null {
  const base = baseHref();
  const abs = new URL(url, globalThis.document?.baseURI ?? base);
  abs.search = '';
  abs.hash = '';
  return abs.href.startsWith(base) ? decodeURI(abs.href.slice(base.length)) : null;
}

let loading: Promise<AssetsManifest | null> | null = null;
let loaded: AssetsManifest | null = null;

/** The manifest (fetched once a page; null when there is none). */
export function assetsManifest(): Promise<AssetsManifest | null> {
  loading ??= (async () => {
    try {
      const res = await fetch(new URL(MANIFEST_FILE, baseHref()), { cache: 'no-cache' });
      if (!res.ok) return null;
      const m = (await res.json()) as Partial<AssetsManifest>;
      return (loaded = m && typeof m.files === 'object' && m.files ? (m as AssetsManifest) : null);
    } catch {
      // No manifest (the dev server's fallback page is not JSON): no caching by hash.
      return null;
    }
  })();
  return loading;
}

/** A file's entry, once the manifest has loaded. */
export const assetEntry = (path: string): AssetEntry | undefined => loaded?.files[path];

/**
 * The URL to ask the server for a file by: `<path>?v=<hash>`, so the server and any cache between
 * may keep it forever, or the plain path while the manifest is not loaded or does not list it.
 */
export function assetUrl(path: string): string {
  const e = assetEntry(path);
  const url = new URL(path, baseHref());
  if (e) url.searchParams.set('v', e.hash);
  return url.href;
}
